/* Created by Interactive 63 - https://interactive63.com */
/**
 * Builds the right input for a property's value type and handles showing several values at
 * once, with "Mixed" for components that differ. Used by property rows and the keyframe section.
 */
import { parseColour, formatColour } from '../../../core/colour';
import type { DrawValue, PropertyValue, Vec2 } from '../../../core/model/types';
import { isDrawValue, isMorphValue, isVec2 } from '../../../core/model/types';
import { shapeThumbnail } from '../../morph/thumbnail';
import type { PropertyDefinition } from '../../../core/properties/registry';
import { h } from '../../ui/dom';
import { ColourInput } from './inputs/colour-input';
import { DrawInput } from './inputs/draw-input';
import { NumberInput } from './inputs/number-input';
import { Vec2Input } from './inputs/vec2-input';

export type ValuePatch = number | string | Partial<Vec2> | Partial<DrawValue>;

export interface ValueEditor {
  el: HTMLElement;
  /**
   * Shows one or more values (several when multiple elements are selected). Fields being edited
   * are left alone unless `force` is set, which is used right after an edit is committed.
   */
  show(values: readonly PropertyValue[], force?: boolean): void;
  setReadOnly(readOnly: boolean): void;
}

export interface ValueEditorHandlers {
  scrubKey: string;
  onChange: (patch: ValuePatch, coalesceKey?: string) => void;
  onDrawOn?: () => void;
  /** Opens the menu for choosing a new shape (morph only). */
  onSetShape?: (anchor: HTMLElement) => void;
  documentColours: () => string[];
}

/** Merges an edit into a current value: scalars are replaced, objects merged per component. */
export function applyPatch(current: PropertyValue, patch: ValuePatch): PropertyValue {
  if (typeof patch === 'number' || typeof patch === 'string') return patch;
  if (isVec2(current)) return { ...current, ...patch };
  if (isDrawValue(current)) return { ...current, ...patch };
  return patch as PropertyValue;
}

/** Returns the shared value, or null when the values differ. */
function shared<T>(values: readonly T[]): T | null {
  const first = values[0];
  if (first === undefined) return null;
  return values.every((v) => v === first) ? first : null;
}

/** Creates an editor for a property's value type. */
export function createValueEditor(
  definition: PropertyDefinition,
  handlers: ValueEditorHandlers,
): ValueEditor {
  const range = { min: definition.min, max: definition.max, step: definition.step };

  switch (definition.valueType) {
    case 'number': {
      const input = new NumberInput({
        ...range,
        unit: definition.unit,
        scrubKey: handlers.scrubKey,
        label: definition.label.charAt(0),
        onChange: (value, key) => {
          handlers.onChange(value, key);
        },
      });
      return {
        el: input.el,
        show: (values, force) => {
          input.setValue(shared(values.filter((v): v is number => typeof v === 'number')), force);
        },
        setReadOnly: (readOnly) => {
          input.setReadOnly(readOnly);
        },
      };
    }
    case 'vec2': {
      const input = new Vec2Input({
        ...range,
        unit: definition.unit,
        linkable: definition.key === 'scale',
        scrubKey: handlers.scrubKey,
        onChange: (patch, key) => {
          handlers.onChange(patch, key);
        },
      });
      return {
        el: input.el,
        show: (values, force) => {
          const vecs = values.filter(isVec2);
          input.setValue(
            { x: shared(vecs.map((v) => v.x)), y: shared(vecs.map((v) => v.y)) },
            force,
          );
        },
        setReadOnly: (readOnly) => {
          input.setReadOnly(readOnly);
        },
      };
    }
    case 'colour': {
      const input = new ColourInput({
        scrubKey: handlers.scrubKey,
        documentColours: handlers.documentColours,
        onChange: (value, key) => {
          handlers.onChange(value, key);
        },
      });
      return {
        el: input.el,
        show: (values, force) => {
          const colours = values.filter((v): v is string => typeof v === 'string');
          // Compare normalised forms so "red" and "#ff0000" count as the same colour.
          const normalised = colours.map((c) => {
            const parsed = parseColour(c);
            return parsed ? formatColour(parsed) : c;
          });
          input.setValue(shared(normalised), force);
        },
        setReadOnly: (readOnly) => {
          input.setReadOnly(readOnly);
        },
      };
    }
    case 'draw': {
      const input = new DrawInput({
        scrubKey: handlers.scrubKey,
        onChange: (patch, key) => {
          handlers.onChange(patch, key);
        },
        onDrawOn: () => {
          handlers.onDrawOn?.();
        },
      });
      return {
        el: input.el,
        show: (values, force) => {
          const draws = values.filter(isDrawValue);
          input.setValue(
            { start: shared(draws.map((v) => v.start)), end: shared(draws.map((v) => v.end)) },
            force,
          );
        },
        setReadOnly: (readOnly) => {
          input.setReadOnly(readOnly);
        },
      };
    }
    case 'text': {
      const area = h('textarea', {
        class: 'words-input',
        rows: 2,
        spellcheck: 'false',
        'aria-label': definition.label,
      });
      area.addEventListener('input', () => {
        handlers.onChange(area.value, handlers.scrubKey);
      });
      return {
        el: area,
        show: (values, force) => {
          if (document.activeElement === area && !force) return;
          const words = shared(values.filter((v): v is string => typeof v === 'string'));
          area.value = words ?? '';
          area.placeholder = words === null ? 'Mixed' : '';
          area.rows = Math.min(6, Math.max(2, area.value.split('\n').length));
        },
        setReadOnly: (readOnly) => {
          area.readOnly = readOnly;
        },
      };
    }
    case 'morph': {
      const picture = h('span', { class: 'morph-picture' });
      const button = h('button', { type: 'button', class: 'morph-set' }, 'Set shape…');
      button.addEventListener('click', () => {
        handlers.onSetShape?.(button);
      });
      button.hidden = handlers.onSetShape === undefined;
      return {
        el: h('span', { class: 'morph-editor' }, picture, button),
        show: (values) => {
          const first = values[0];
          const same = values.every(
            (v) => isMorphValue(v) && isMorphValue(first) && v.d === first.d,
          );
          picture.replaceChildren(isMorphValue(first) && same ? shapeThumbnail(first.d) : '');
        },
        setReadOnly: (readOnly) => {
          button.disabled = readOnly;
        },
      };
    }
  }
}
